// 세션 도구 막대의 [⋯ 더보기] 메뉴(2026-10-12 사용자 요구 — "꼭 필요하고 자주 쓰는 것만 남기고 나머지는 메뉴로").
//
// 막대에 남는 것 = [파일] · [전체화면] · [⋯] · [연결 종료]. 나머지(채팅 · 그리기 · 녹화 · 화질/FPS · 클립보드 · 접속 정보)는 여기로.
// 녹화 중에는 SessionView 가 막대에 따로 **상태 표시**를 남긴다 — 메뉴 안에 숨으면 켜 둔 것을 잊는다
// (그리기는 켜져 있는 동안 화면 위 그리기 막대가 이미 보인다).
// 안 읽은 채팅 수는 [⋯] 단추 모서리 숫자로 보인다(`badge`).
//
// 항목은 **데이터로** 받는다(`MoreItem[]`) — 항목을 더하거나 순서를 바꿀 때 SessionView 의 목록만 고치면 된다.
import type { ReactNode } from 'react';
import { BarMenu } from './BarMenu';
import { QualityControls } from './QualityControls';
import { ClipboardMenuSection } from './ClipboardControls';
import type { QualityView } from '../quality/useQuality';
import type { ClipboardShare } from '../clipboard/useClipboardShare';

/** [⋯] 메뉴의 한 항목. */
export interface MoreItem {
  key: string;
  icon: ReactNode;
  label: string;
  /** 항목 아래 작은 설명. */
  hint?: string;
  /** 켜져 있음(✓ 표시). 켬/끔 항목만 준다. */
  on?: boolean;
  /** 항목 옆 숫자(예: 안 읽은 채팅). */
  badge?: number;
  disabled?: boolean;
  /** 누른 뒤 메뉴를 열어 둔다 — 켬/끔처럼 결과를 그 자리에서 봐야 하는 항목. 기본 = 닫는다. */
  keepOpen?: boolean;
  onSelect: () => void | Promise<void>;
}

/** [⋯] 단추 풍선 도움말 — 메뉴 안에 무엇이 있는지 열지 않고도 알 수 있게. */
const MORE_TITLE = '더보기 — 채팅 · 그리기 · 녹화 · 화질 · 클립보드 · 접속 정보';

export function SessionMoreMenu({
  items,
  quality,
  clip,
  badge,
  refocus,
}: {
  items: MoreItem[];
  quality: QualityView;
  clip: ClipboardShare;
  /** [⋯] 단추 모서리 숫자(메뉴 안 항목들의 알림 합). */
  badge?: number;
  refocus: () => void;
}) {
  return (
    <BarMenu label="⋯" title={MORE_TITLE} badge={badge} onClosed={refocus}>
      {(close) => (
        <>
          <div className="bar-menu-section">
            {items.map((it) => (
              <button
                key={it.key}
                className={`ghost${it.on ? ' on' : ''}`}
                role={it.on === undefined ? 'menuitem' : 'menuitemcheckbox'}
                aria-checked={it.on}
                disabled={it.disabled}
                onClick={() => {
                  const p = it.onSelect();
                  if (!it.keepOpen) {
                    close();
                  }
                  void p;
                }}
              >
                <span className="bar-menu-row">
                  <span className="bar-menu-icon">{it.icon}</span>
                  <span className="bar-menu-label">{it.label}</span>
                  {!!it.badge && it.badge > 0 && <span className="badge">{it.badge}</span>}
                  {it.on && <span className="bar-menu-check-mark">✓</span>}
                </span>
                {it.hint && <small>{it.hint}</small>}
              </button>
            ))}
          </div>
          {quality.available && (
            <div className="bar-menu-section">
              <div className="bar-menu-head">화질</div>
              <div className="bar-menu-inline">
                <QualityControls quality={quality} refocus={refocus} />
              </div>
            </div>
          )}
          <ClipboardMenuSection clip={clip} close={close} />
        </>
      )}
    </BarMenu>
  );
}
